import { AjaxClient } from "app/common/ajax";
import { deleteCookie, readCookie, writeCookie } from "app/common/cookies";
import * as $ from "jquery";
import * as moment from "moment";
import { ISignal, ISimpleEvent, SignalDispatcher, SimpleEventDispatcher } from "strongly-typed-events";

const muteCookieKey = "notification-mute";
const muteCookieValue = "mute";
const muteCookieTtl = moment.duration(1, "h");

export class NotificationManager {
    private readonly notifications = new Map<number, JQuery<HTMLElement>>();
    private readonly notificationAddedDispatcher = new SimpleEventDispatcher<INotification>();
    private readonly notificationRemovedDispatcher = new SignalDispatcher();

    private parentElement: JQuery<HTMLElement>;
    private unshownNotificationCount: number;

    public constructor(private readonly ajaxClient: AjaxClient) {}

    public get notificationAdded(): ISimpleEvent<INotification> {
        return this.notificationAddedDispatcher.asEvent();
    }

    public get notificationRemoved(): ISignal {
        return this.notificationRemovedDispatcher.asEvent();
    }

    public get shownNotificationCount(): number {
        return this.notifications.size;
    }

    public get totalNotificationCount(): number {
        return this.shownNotificationCount + this.unshownNotificationCount;
    }

    public get areNotificationsMuted(): boolean {
        return readCookie(muteCookieKey) === muteCookieValue;
    }

    public synchroniseWithHtmlElement(parentElmt: JQuery<HTMLElement>): void {
        this.parentElement = parentElmt;

        this.notifications.clear();

        this.parentElement.find("li").each((_, elmt) => {
            const domId = elmt.id; // notification-xx where xx is the numeric id
            const notificationId = parseInt(domId.substr("notification-".length), 10);
            this.notifications.set(notificationId, $(elmt));

            if (this.notifications.get(notificationId).data("timeout")) {
                this.scheduleNotificationRemoval(notificationId);
            }
        });
    }

    public setNotificationsFromRemote(newNotifications: INotification[], totalCount: number): void {
        const existingIds = Array.from(this.notifications.keys());
        const newIds = newNotifications.map((n) => n.notification_id);

        const addedNotifications = newNotifications.filter((n) => !existingIds.includes(n.notification_id));
        const removedIds = existingIds.filter((id) => !newIds.includes(id));

        addedNotifications.forEach((n) => this.addNotification(n));
        removedIds.forEach((id) => this.removeNotificationAnimated(id));

        this.unshownNotificationCount = totalCount - this.notifications.size;
    }

    public async dismissNotification(notificationId: number): Promise<void> {
        this.removeNotification(notificationId);
        // eslint-disable-next-line @typescript-eslint/naming-convention -- remote API
        await this.ajaxClient.call("/notifications/remove", {notification_id: notificationId});
    }

    public muteNotifications(): void {
        writeCookie(muteCookieKey, muteCookieValue, muteCookieTtl);
    }

    public unmuteNotifications(): void {
        deleteCookie(muteCookieKey);
    }

    private addNotification(notification: INotification): void {
        const elmt = $(notification.html).hide();
        this.notifications.set(notification.notification_id, elmt);
        this.parentElement.append(elmt);
        elmt.fadeIn();

        this.notificationAddedDispatcher.dispatch(notification);

        if (elmt.data("timeout")) {
            this.scheduleNotificationRemoval(notification.notification_id);
        }
    }

    private scheduleNotificationRemoval(notificationId: number): void {
        const elmt = this.notifications.get(notificationId);
        const timeout = parseInt(elmt.data("timeout") as string, 10);
        if (timeout < 0) {
            return;
        }
        setTimeout(
            () => this.removeNotification(notificationId),
            timeout * 1000);
    }

    private removeNotificationAnimated(notificationId: number): void {
        const elmt = this.notifications.get(notificationId);
        this.notifications.delete(notificationId);
        elmt.fadeOut(() => elmt.remove());

        this.notificationRemovedDispatcher.dispatch();
    }

    private removeNotification(notificationId: number): void {
        const elmt = this.notifications.get(notificationId);
        this.notifications.delete(notificationId);
        elmt.remove();

        this.notificationRemovedDispatcher.dispatch();
    }
}

export interface INotification {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- remote API
    notification_id: number;
    html: string;
}
